import { useRef, useState } from "react";
import { MLink } from "@/lib/marketing/m-link";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { icon } from "@/lib/icons/icon";
import { ChevronDownIcon as g_ChevronDownIcon } from "@hugeicons/core-free-icons";
import { NavLink } from "@/components/marketing/site/marketing-shell-types";

/* Straight from the icon package rather than lib/icons, which would load
   every glyph in the app on every page this renders on (see lib/icons/icon.tsx). */
const ChevronDown = icon(g_ChevronDownIcon);

/** How long the menu stays open after the pointer leaves it, so a diagonal
 *  move from the trigger into the panel does not close it on the way. */
const RESOURCES_CLOSE_DELAY_MS = 140;

/** Opens on hover for a mouse and on click or keyboard otherwise; Radix owns click, focus and
 * Escape. `modal={false}` keeps the page scrollable and the header clickable while open. */
export function HangingMenu({
  label,
  links,
  testId,
}: {
  label: string;
  links: NavLink[];
  testId: string;
}) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number>(0);
  const show = () => {
    window.clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hide = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), RESOURCES_CLOSE_DELAY_MS);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild onMouseEnter={show} onMouseLeave={hide}>
        <Button variant="ghost" size="sm" className="font-ui" data-testid={`button-${testId}`}>
          {label}
          <ChevronDown
            className={`ml-1 size-3.5 transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={6}
        className="w-48 font-ui"
        onMouseEnter={show}
        onMouseLeave={hide}
        data-testid={`menu-${testId}`}
      >
        {links.map((l) => (
          <DropdownMenuItem key={l.href} asChild className="cursor-pointer">
            <MLink href={l.href} data-testid={l.testId} onClick={() => setOpen(false)}>
              {l.label}
            </MLink>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
